import { useState, useMemo } from 'react';
import { Copy } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Switch } from '@/components/ui/Switch';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

export function UrlEncode() {
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [input, setInput] = useState('https://example.com/path?q=hello world&page=2');
  const [component, setComponent] = useState(false);

  const output = useMemo(() => {
    if (!input) return '';
    try {
      if (mode === 'encode') {
        return component ? encodeURIComponent(input) : encodeURI(input);
      }
      return decodeURIComponent(input);
    } catch {
      return '⚠ Invalid input';
    }
  }, [input, mode, component]);

  return (
    <div>
      <ToolHeader
        toolId="url-encode"
        title="URL encoder / decoder"
        description="Percent-encode and decode URL strings. Toggle between full-URI and component modes."
      />

      <Tabs value={mode} onValueChange={(v) => setMode(v as 'encode' | 'decode')}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <TabsList>
            <TabsTrigger value="encode">Encode</TabsTrigger>
            <TabsTrigger value="decode">Decode</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-fg-muted">Component-safe</span>
            <Switch checked={component} onChange={setComponent} />
          </div>
        </div>

        <TabsContent value="encode" />
        <TabsContent value="decode" />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="!p-0">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
              <div className="text-xs font-medium text-fg-muted">Input</div>
              <Button size="sm" variant="ghost" onClick={() => setInput('')}>
                Clear
              </Button>
            </div>
            <Textarea
              mono
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="!border-0 !rounded-t-none min-h-[280px] !bg-transparent focus-visible:!ring-0"
            />
          </Card>

          <Card className="!p-0">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
              <div className="text-xs font-medium text-fg-muted">Output</div>
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  const ok = await copyToClipboard(output);
                  if (ok) toast.success('Copied');
                }}
                disabled={!output}
              >
                <Copy size={11} />
              </Button>
            </div>
            <pre className="font-mono text-[13px] leading-relaxed p-4 overflow-auto min-h-[280px] break-all whitespace-pre-wrap">
              <code>{output}</code>
            </pre>
          </Card>
        </div>
      </Tabs>
    </div>
  );
}

export default UrlEncode;
